import type {
  ColumnOrderState,
  ColumnSizingState,
  Table,
  Updater,
  VisibilityState,
} from '@tanstack/react-table';
import { useCallback } from 'react';

export type ColumnPersistenceSnapshot = {
  columnVisibility?: VisibilityState | { state?: VisibilityState };
  columnOrder?: ColumnOrderState;
  columnSizing?: ColumnSizingState | { state?: ColumnSizingState };
};

export function getColumnStorageSlot(tableId: string): string {
  return `columns-${tableId}`;
}

function unwrapVisibilityState(
  value: VisibilityState | { state?: VisibilityState },
): { state: VisibilityState; nested: boolean } {
  if (typeof value === 'object' && value !== null && 'state' in value && value.state !== undefined) {
    return { state: value.state as VisibilityState, nested: true };
  }
  return { state: value as VisibilityState, nested: false };
}

function unwrapColumnSizingState(
  value: ColumnSizingState | { state?: ColumnSizingState },
): { state: ColumnSizingState; nested: boolean } {
  if (typeof value === 'object' && value !== null && 'state' in value && value.state !== undefined) {
    return { state: value.state as ColumnSizingState, nested: true };
  }
  return { state: value as ColumnSizingState, nested: false };
}

/**
 * Persists column order, visibility and sizing to `localStorage`.
 * Pair with {@link restoreColumnState} after the TanStack table instance is ready.
 */
export function useColumnPersistence(tableId: string) {
  const storageSlot = getColumnStorageSlot(tableId);

  const saveColumns = useCallback(
    (newState: Partial<ColumnPersistenceSnapshot>) => {
      const savedStateStr = localStorage.getItem(storageSlot);
      const savedState = savedStateStr ? JSON.parse(savedStateStr) : {};
      const state = { ...savedState, ...newState };

      localStorage.setItem(
        storageSlot,
        JSON.stringify({
          columnVisibility: state.columnVisibility,
          columnOrder: state.columnOrder,
          columnSizing: state.columnSizing,
        }),
      );
    },
    [storageSlot],
  );

  const restoreColumnState = useCallback(
    (table: Table<unknown>) => {
      const store = localStorage.getItem(storageSlot);
      if (!store) return;

      const storedState = JSON.parse(store) as ColumnPersistenceSnapshot;
      if (!storedState) return;

      const curState = table.getState();

      if (storedState.columnVisibility) {
        const { state: stor, nested } = unwrapVisibilityState(storedState.columnVisibility);
        if (nested) saveColumns({ columnVisibility: stor });
        const cur = curState.columnVisibility;
        if (JSON.stringify(stor) !== JSON.stringify(cur)) {
          table.setColumnVisibility(stor);
        }
      }

      if (storedState.columnOrder) {
        const stor = storedState.columnOrder;
        const cur = curState.columnOrder;
        if (JSON.stringify(stor) !== JSON.stringify(cur)) {
          table.setColumnOrder(stor);
        }
      }

      if (storedState.columnSizing) {
        const { state: stor, nested } = unwrapColumnSizingState(storedState.columnSizing);
        if (nested) saveColumns({ columnSizing: stor });
        const cur = curState.columnSizing;
        if (JSON.stringify(stor) !== JSON.stringify(cur)) {
          table.setColumnSizing(stor);
        }
      }
    },
    [saveColumns, storageSlot],
  );

  return { saveColumns, restoreColumnState, storageSlot };
}

/** Wrap TanStack column updaters and persist the resolved state. */
export function resolveColumnUpdater<T>(updater: Updater<T>, current: T): T {
  return typeof updater === 'function' ? (updater as (old: T) => T)(current) : updater;
}
